import Box from '@mui/material/Box'
import LoadingButton from '@mui/material/Button'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import type React from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { churnCustomerUsingPOST } from '../api'
import Spacings from '../figma/tokens/Spacings'
import FigmaBox from '../mynt-components/components/FigmaBox'
import { useCustomerNotesDataProvider } from './CustomerNotes'
import { CoreMyntModal } from './modals/CoreMyntModal'
import { TextFieldController } from './react-hook-components'

const CHURN_CONSEQUENCES = [
  'All persons will be deleted.',
  'All cards will be terminated.',
  'Subscription fee will be set to zero.',
  'KYC status will be revoked.',
  {
    condition: 'If there is no corporate account:',
    items: ['All card products and features will be turned off.'],
  },
  {
    condition: 'If remaining corporate account balance is zero:',
    items: [
      'The corporate account will be closed.',
      'Card credit will be inactivated.',
      'All card products and features will be turned off.',
      'Auto top-up will be disabled.',
    ],
  },
  {
    condition: 'If remaining corporate account balance is not zero:',
    items: [
      'The corporate account will remain open.',
      'Card credit will be inactivated.',
      'All card products and features will be turned off.',
      'Auto top-up will be disabled.',
    ],
  },
  'A negative balance will be invoiced in the monthly batch job if invoicing is active on the card credit.',
  'No automatic payback will be done for a positive balance, this has to be done manually.',
  'No confirmation email will automatically be sent to the customer, this has to be done manually.',
]

interface ChurnFormValues {
  confirmed: boolean
  reason: string
}

interface Props {
  customerId: string
  companyName: string
  isOpen: boolean
  onCancel: () => void
  onSuccess: () => void
}

const ChurnCustomerModal: React.FC<Props> = ({ customerId, companyName, isOpen, onCancel, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false)

  const customerNotesProps = useCustomerNotesDataProvider(customerId, { enabled: true })

  const form = useForm<ChurnFormValues>({
    defaultValues: {
      confirmed: false,
      reason: '',
    },
  })

  const isConfirmed = form.watch('confirmed')
  const churnReason = form.watch('reason')

  const onSubmit = async (data: ChurnFormValues) => {
    if (!data.confirmed || !data.reason.trim()) {
      return
    }

    setIsLoading(true)

    try {
      await churnCustomerUsingPOST(customerId)
      await customerNotesProps.onCreateNote('Customer churned with churn reason: ' + data.reason)

      form.reset()
      onSuccess()
    } catch (err) {
      console.warn(err)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <CoreMyntModal
      open
      title="Are you sure you want to churn this customer?"
      actions={(Wrapper) => (
        <Box sx={{ color: 'white' }}>
          <Wrapper>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <LoadingButton
              disabled={!isConfirmed || !churnReason.trim()}
              variant="primary"
              onClick={form.handleSubmit(onSubmit)}
              loading={isLoading}
            >
              Churn
            </LoadingButton>
          </Wrapper>
        </Box>
      )}
      onClose={onCancel}
    >
      <Box sx={{ display: 'flex', gap: '16px', paddingTop: '16px' }}>
        <FigmaBox gap={Spacings.small} fullWidth>
          <Box>
            <Typography variant="body2" gutterBottom>
              Please review the consequences:
            </Typography>
            <List sx={{ listStyleType: 'disc', pl: 4 }}>
              {CHURN_CONSEQUENCES.map((item) => {
                if (typeof item === 'string') {
                  return (
                    <ListItem key={item} sx={{ display: 'list-item', p: 0 }}>
                      <ListItemText primary={item} slotProps={{ primary: { sx: { fontSize: '16px' } } }} />
                    </ListItem>
                  )
                }
                return (
                  <ListItem key={item.condition} sx={{ display: 'list-item', p: 0 }}>
                    <Typography variant="body1" sx={{ fontSize: '16px' }}>
                      {item.condition}
                    </Typography>
                    <List sx={{ listStyleType: 'circle', pl: 4 }}>
                      {item.items.map((subText) => (
                        <ListItem key={subText} sx={{ display: 'list-item', p: 0 }}>
                          <ListItemText primary={subText} slotProps={{ primary: { sx: { fontSize: '16px' } } }} />
                        </ListItem>
                      ))}
                    </List>
                  </ListItem>
                )
              })}
            </List>
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isConfirmed}
                  onChange={(event) => {
                    form.setValue('confirmed', event.target.checked)
                  }}
                />
              }
              label={<Typography variant="body2">Yes, I want to churn customer {companyName}</Typography>}
            />
          </Box>
          {isConfirmed && <TextFieldController labelText="Churn reason" control={form.control} name="reason" multiline />}
        </FigmaBox>
      </Box>
    </CoreMyntModal>
  )
}

export default ChurnCustomerModal
